import { main_url } from "../pages/basefile";
import { getValidToken } from "../auth/getToken";

async function getAuthHeaders() {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchClientDatagridRows({
  page = 0,
  pageSize = 10,
  search = "",
  sortField = "cid",
  sortOrder = "asc",
}) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    search,
    sortField,
    sortOrder,
  });

  const response = await fetch(
    `${main_url}/api/v1/clients/datagrid/rows?${params.toString()}`,
    {
      method: "GET",
      headers: await getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch clients");
  }

  return response.json();
}

export async function deleteClient(cid) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete client");
  }
}

export async function createClient(payload) {
  const response = await fetch(`${main_url}/api/v1/clients`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to create client");
  }

  return response.json();
}

export async function generateClientCid(name) {
  const params = new URLSearchParams({ name });
  const response = await fetch(`${main_url}/api/v1/clients/generate-cid?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to generate CID");
  }

  return response.json();
}

export async function getClientByCid(cid) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch client details");
  }

  return response.json();
}

export async function updateClient(cid, payload) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}`, {
    method: "PUT",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update client");
  }

  return response.json();
}

export async function fetchClientProjectsSummary(cid) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}/projects-summary`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch associated projects");
  }

  return response.json();
}

export async function fetchClientDocuments(cid) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}/documents`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch client documents");
  }

  return response.json();
}

export async function uploadClientDocument(cid, { doctype, document_name, file }) {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  const formData = new FormData();
  formData.append("doctype", doctype);
  formData.append("document_name", document_name);
  formData.append("file", file);

  const response = await fetch(`${main_url}/api/v1/clients/${cid}/documents`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to upload client document");
  }

  return response.json();
}

export async function deleteClientDocument(cid, documentId) {
  const response = await fetch(`${main_url}/api/v1/clients/${cid}/documents/${documentId}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete client document");
  }
}

export function getClientDocumentDownloadUrl(cid, documentId) {
  return `${main_url}/api/v1/clients/${cid}/documents/${documentId}/download`;
}

export async function fetchClientDropdown({ search = "" } = {}) {
  const params = new URLSearchParams({ search });
  const response = await fetch(`${main_url}/api/v1/clients/dropdown?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch client dropdown");
  }

  return response.json();
}
